import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import {
	FLUSH,
	PAUSE,
	PERSIST,
	persistReducer,
	persistStore,
	PURGE,
	REGISTER,
	REHYDRATE,
} from 'redux-persist';
import createSagaMiddleware from 'redux-saga';

import { AssignmentHistoryLayerReducer } from './assignment-history-layer';
import { AuthReducer } from './auth';
import { CommonReducer } from './common';
import { CommonInquiryBoardReducer } from './common-inquiry-board';
import { CommonInquiryBoardAnswerReducer } from './common-inquiry-board-answer';
import { CommonNoticeReducer } from './common-notice';
import { CommonNoticeReceiveReducer } from './common-notice-receive';
import { ContractedForeignBusinessReducer } from './contracted-foreign-business';
import { DefineDocumentAreaReducer } from './define-document-area';
import { DefineDocumentDownloadReducer } from './define-document-download';
import { DefineDocumentUploadReducer } from './define-document-upload';
import { DeviceLogReducer } from './device-log';
import { DeviceMobileReducer } from './device-mobile';
import { DeviceTabletReducer } from './device-tablet';
import { DistrictAllowIpReducer } from './district-allow-ip';
import { Fm1_01DefaultInfoUnitReducer } from './fm1-01-default-info-unit';
import { FooterInfoReducer } from './footer-info';
import { ForeignApplicantReducer } from './foreign-applicant/foreign-applicant.redux';
import { ForeignDashboardReducer } from './foreign-dashboard';
import { OI1_01DefaultInfoUnitReducer } from './foreign-default-info-unit';
import { ForeignProgramReducer } from './foreign-program';
import { ForeignSystemUnitReducer } from './foreign-system-unit';
import { ForeignUserReducer } from './foreign-user';
import { LanguageReducer } from './language';
import { MSelectLodgingPopupReducer } from './m-select-lodging-popup';
import { MyPageReducer } from './my-page';
import { AssignmentCalendarReducer } from './pa2-01-assignment-calendar/index';
import { PublicWorkHistoryLayerReducer } from './public-work-history-layer';
import { SearchFilterReducer } from './search-filter';
import { persistConfig } from './store.persist';
import rootSaga from './store.saga';
import { SystemUnitReducer } from './system-unit';
import { SystemUseTipReducer } from './system-use-tip';
import { SystemUserReducer } from './system-user';
import { UnitSettingReducer } from './us1-01-unit-setting';
import { WorkHistoryLayerReducer } from './work-history-layer';
import { WorkerApplicantReducer } from './worker-applicant';
import { WorkerAssignmentReducer } from './worker-assignment';
import { WorkerAssignmentFranchiseReducer } from './worker-assignment-franchise';
import { WorkerAssignmentPublicReducer } from './worker-assignment-public';
import { WorkerAuditMonitoringReducer } from './worker-audit-monitoring';
import { WorkerChangeWorkPlaceReducer } from './worker-change-work-place';
import { WorkerDashboardReducer } from './worker-dashboard';
import { WorkerDocumentReducer } from './worker-document';
import { WorkerExtensionStayReducer } from './worker-extension-stay';
import { WorkerFranchiseReducer } from './worker-franchise';
import { WorkerHistoryReducer } from './worker-history';
import { WorkerLodgingReducer } from './worker-lodging';
import { WorkerProgramReducer } from './worker-program';
import { WorkerProgramStatusReducer } from './worker-program-status';
import { WorkerPublicSalaryReducer } from './worker-public-salary/worker-public-salary.redux';
import { WorkerRecommenderReducer } from './worker-recommender';
import { WorkerSalaryReducer } from './worker-salary';
import { WorkerUnauthorizedDepartureReducer } from './worker-unauthorized-departure';
import { WorkerUseTipReducer } from './worker-use-tip';
import { WorkerUserReducer } from './worker-user';

const rootReducer = combineReducers({
	footerInfo: FooterInfoReducer,
	language: LanguageReducer,
	mSelectLodgingPopup: MSelectLodgingPopupReducer,
	workerProgramStatus: WorkerProgramStatusReducer,
	searchFilter: SearchFilterReducer,
	workerDashboard: WorkerDashboardReducer,
	workerExtensionStay: WorkerExtensionStayReducer,
	assignmentHistoryLayer: AssignmentHistoryLayerReducer,
	workerUnauthorizedDeparture: WorkerUnauthorizedDepartureReducer,
	workerAssignment: WorkerAssignmentReducer,
	workerAssignmentFranchise: WorkerAssignmentFranchiseReducer,
	workerAssignmentPublic: WorkerAssignmentPublicReducer,
	workerProgram: WorkerProgramReducer,
	workerDocument: WorkerDocumentReducer,
	workerSalary: WorkerSalaryReducer,
	workerFranchise: WorkerFranchiseReducer,
	workerPublicSalary: WorkerPublicSalaryReducer,
	defineDocumentArea: DefineDocumentAreaReducer,
	defineDocumentDownload: DefineDocumentDownloadReducer,
	fm1_01DefaultInfoUnit: Fm1_01DefaultInfoUnitReducer,
	oi1_01DefaultInfoUnit: OI1_01DefaultInfoUnitReducer,
	workerLodging: WorkerLodgingReducer,
	workerRecommender: WorkerRecommenderReducer,
	us1_01UnitSetting: UnitSettingReducer,
	workerUser: WorkerUserReducer,
	workerApplicant: WorkerApplicantReducer,
	am2_01AssignmentCalendar: AssignmentCalendarReducer,
	systemUser: SystemUserReducer,
	systemUnit: SystemUnitReducer,
	common: CommonReducer,
	commonNotice: CommonNoticeReducer,
	commonNoticeReceive: CommonNoticeReceiveReducer,
	commonInquiryBoard: CommonInquiryBoardReducer,
	commonInquiryBoardAnswer: CommonInquiryBoardAnswerReducer,
	auth: AuthReducer,
	myPage: MyPageReducer,
	workerChangeWorkPlace: WorkerChangeWorkPlaceReducer,
	defineDocumentUpload: DefineDocumentUploadReducer,
	deviceMobile: DeviceMobileReducer,
	deviceTablet: DeviceTabletReducer,
	deviceLog: DeviceLogReducer,
	districtAllowIp: DistrictAllowIpReducer,
	workHistoryLayer: WorkHistoryLayerReducer,
	foreignUser: ForeignUserReducer,
	foreignApplicant: ForeignApplicantReducer,
	foreignDashboard: ForeignDashboardReducer,
	foreignProgram: ForeignProgramReducer,
	contractedForeignBusiness: ContractedForeignBusinessReducer,
	foreignSystemUnit: ForeignSystemUnitReducer,
	workerUseTip: WorkerUseTipReducer,
	systemUseTip: SystemUseTipReducer,
	workerHistory: WorkerHistoryReducer,
	publicWorkHistoryLayer: PublicWorkHistoryLayerReducer,
	workerAuditMonitoring: WorkerAuditMonitoringReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);
const sagaMiddleware = createSagaMiddleware();
const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
				ignoredActionPaths: [
					'payload.callback',
					'payload.callbackSuccess',
					'payload.data',
				],
			},
		}).concat(sagaMiddleware),
});

const persistor = persistStore(store);

sagaMiddleware.run(rootSaga);

export { persistor, store };
